/**
 * The event dialog is opened from five places (`080-calendar.md` §9): the palette's `>` mode,
 * the agenda's `+`, a month cell, an hour slot in the week view, and an existing event. None
 * of them owns it, and one of them (the palette) is mounted in the shell, three routes away
 * from the calendar.
 *
 * So the dialog is a HOST mounted once in the shell, driven by a tiny external store — the
 * same shape as `offline/conflicts.ts`, and for the same reason: the openers are not all
 * components, and a context provider around the whole app for one dialog would be worse.
 */
import type { Violation } from '@/api/client';
import type { CalendarEvent } from '@/api/endpoints/calendar';

export interface EventDraft {
  /** `YYYY-MM-DD` in the user's timezone. Prefilled by whatever opened the dialog. */
  dayKey?: string;
  /** Minutes since local midnight. Set by the week view's hour slots. */
  minutes?: number;
  /** The end of a range DRAGGED in the week view (`080` §9). Absent → a 60-minute default. */
  endMinutes?: number;
  allDay?: boolean;
  /** Pre-link the event to a page — used by "New event" opened from a page. */
  pageIds?: string[];
}

export interface EventDialogState {
  mode: 'create' | 'edit';
  /** Present in edit mode. */
  event?: CalendarEvent;
  draft?: EventDraft;
  /**
   * Server-side violations from a `422` the outbox discovered at delivery time (`080` §9:
   * "the client check is UX, not enforcement"). The dialog re-opens carrying them, so the
   * message is shown INLINE on the offending field rather than only in a toast.
   */
  violations?: Violation[];
  /** The failed outbox entry this dialog is here to replace, if any. */
  entryId?: number;
}

type Listener = () => void;

const listeners = new Set<Listener>();
let state: EventDialogState | null = null;

function emit(next: EventDialogState | null): void {
  state = next;
  for (const listener of listeners) listener();
}

export function subscribeEventDialog(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getEventDialog(): EventDialogState | null {
  return state;
}

export function openEventDialog(next: EventDialogState): void {
  emit(next);
}

export function openNewEvent(draft: EventDraft = {}): void {
  emit({ mode: 'create', draft });
}

export function openEditEvent(event: CalendarEvent): void {
  emit({ mode: 'edit', event });
}

export function closeEventDialog(): void {
  emit(null);
}
